/**
 * Matching a practice's spreadsheet headings to Prospera's lead fields.
 *
 * Pure functions only — no React, no network — because this decides where every
 * value in the file ends up, and it should be provable without rendering
 * anything.
 *
 * The field list mirrors the server's LeadImportNormalizer::COLUMN_MAP and
 * LOOKUP_TARGETS, which in turn mirror LeadsController::fieldMap(). Adding a
 * field means adding it in both places; a key the server does not know is
 * ignored rather than stored.
 */

/**
 * The opt-in account module that switches lead import on. It is the lowered
 * catalog title "Lead Import", and must equal the server's
 * LeadImportService::MODULE_KEY: a mismatch hides the screens while the
 * endpoints stay open, or the reverse.
 */
export const LEAD_IMPORT_MODULE_KEY = 'lead import';

/** How a mapped column produces its value. */
export type PopulationMode = 'value' | 'name' | 'id' | 'flag';

/** An account-scoped catalog a lookup field resolves against. */
export type LookupCatalog = 'facility' | 'leadSource' | 'planType' | 'user';

export interface LeadImportField {
  /** Wire key sent to the API. */
  key: string;
  /** What the user picks in the dropdown. */
  label: string;
  /** Shown under the label when the field needs explaining. */
  hint?: string;
  synonyms: string[];
  /** Modes this field accepts, first is the default. Omitted means ['value']. */
  modes?: PopulationMode[];
  /** Which catalog name/id/flag resolve against. */
  catalog?: LookupCatalog;
}

export interface ColumnAssignment {
  /** Lead field this column feeds. null = "Don't import this column". */
  field: string | null;
  /** Always 'value' for a plain text field. */
  mode: PopulationMode;
}

/** One entry per column, positionally aligned with ParsedCsv.headers. */
export type ColumnMapping = ColumnAssignment[];

/**
 * `fullName` is a pseudo-field: a lead has no single name column, but plenty of
 * exports have one, so it is split on the last space when the row is built.
 */
export const FULL_NAME_KEY = 'fullName';

/** Custom lead fields are keyed `custom:{contactFieldID}`. */
export const CUSTOM_FIELD_PREFIX = 'custom:';

export const LEAD_IMPORT_FIELDS: LeadImportField[] = [
  {
    key: 'firstName',
    label: 'First name',
    synonyms: ['firstname', 'first', 'fname', 'givenname', 'forename', 'firstnames'],
  },
  {
    key: 'lastName',
    label: 'Last name',
    synonyms: ['lastname', 'last', 'lname', 'surname', 'familyname'],
  },
  {
    key: FULL_NAME_KEY,
    label: 'Full name',
    hint: 'Use when one column holds the whole name. Split on the last space.',
    synonyms: ['name', 'fullname', 'contactname', 'leadname', 'patientname', 'clientname'],
  },
  {
    key: 'email',
    label: 'Email',
    synonyms: ['email', 'emailaddress', 'email1', 'primaryemail', 'workemail', 'mail'],
  },
  {
    key: 'phone',
    label: 'Phone',
    synonyms: ['phone', 'phonenumber', 'telephone', 'tel', 'homephone', 'primaryphone', 'phone1'],
  },
  {
    key: 'cellPhone',
    label: 'Mobile phone',
    synonyms: ['cell', 'cellphone', 'mobile', 'mobilephone', 'mobilenumber', 'cellular', 'phone2'],
  },
  {
    key: 'workPhone',
    label: 'Work phone',
    synonyms: ['workphone', 'officephone', 'businessphone', 'phone3'],
  },
  {
    key: 'address',
    label: 'Address',
    synonyms: ['address', 'address1', 'streetaddress', 'street', 'addressline1'],
  },
  { key: 'city', label: 'City', synonyms: ['city', 'town', 'suburb'] },
  { key: 'state', label: 'State', synonyms: ['state', 'province', 'region', 'county'] },
  {
    key: 'zip',
    label: 'ZIP or postal code',
    hint: 'Codes containing letters are saved in the notes instead.',
    synonyms: ['zip', 'zipcode', 'postalcode', 'postcode', 'postal'],
  },
  { key: 'gender', label: 'Gender', synonyms: ['gender', 'sex'] },
  {
    key: 'birthDate',
    label: 'Date of birth',
    hint: 'Dates like 03/04/1985 are read as month/day/year.',
    synonyms: ['dob', 'dateofbirth', 'birthdate', 'birthday', 'born'],
  },

  // ─── Lookups ──────────────────────────────────────────────────────────────

  {
    key: 'facilityId',
    label: 'Facility',
    modes: ['name', 'id', 'flag'],
    catalog: 'facility',
    synonyms: ['facility', 'location', 'clinic', 'office', 'branch'],
  },
  {
    // Inherits the lead-source synonyms from the free-text field below: when a
    // customer's file says "Lead Source" they mean the source the CRM filters
    // on, not the website the lead arrived from.
    key: 'leadSourceId',
    label: 'Lead source',
    modes: ['name', 'id', 'flag'],
    catalog: 'leadSource',
    synonyms: [
      'source',
      'leadsource',
      'referralsource',
      'referredby',
      'howdidyouhearaboutus',
      'howdidyouhear',
    ],
  },
  {
    key: 'planTypeId',
    label: 'Plan type',
    modes: ['name', 'id', 'flag'],
    catalog: 'planType',
    synonyms: ['plantype', 'plan', 'producttype', 'programtype'],
  },
  {
    key: 'assignedUserId',
    label: 'Assigned to',
    modes: ['name', 'id', 'flag'],
    catalog: 'user',
    synonyms: ['assignedto', 'assigned', 'owner', 'coordinator', 'salesrep', 'accountowner'],
  },

  // ─── Free text that is easily confused with the lookups above ─────────────

  {
    // Writes contacts.siteReferrer, which is the originating WEBSITE and not
    // the lead source. It kept its key and column; only the label changed, so
    // nothing already imported means anything different.
    key: 'referralSource',
    label: 'Originating website',
    hint: 'The site the lead came from. For the lead source, use the field above.',
    synonyms: ['site', 'website', 'sitereferrer', 'originatingsite', 'landingpage', 'referringurl'],
  },
  {
    key: 'procedureInterest',
    // The leads list calls this column Subject, so the import does too. The key
    // and the column it writes are unchanged.
    label: 'Subject',
    synonyms: ['subject', 'interest', 'interestedin', 'procedure', 'service', 'treatment', 'topics', 'topic'],
  },
  { key: 'notes', label: 'Notes', synonyms: ['notes', 'note', 'comments', 'comment', 'message', 'description', 'additionalinfo'] },
  {
    key: 'dateAdded',
    label: 'Date added',
    hint: 'Defaults to today when this is not matched.',
    synonyms: ['dateadded', 'created', 'createddate', 'datecreated', 'submitted', 'submitteddate'],
  },

  // ─── Consent ──────────────────────────────────────────────────────────────

  {
    key: 'emailOptIn',
    label: 'Email opt-in',
    hint: 'Yes/no. Leave unmapped rather than guessing — consent is not assumed.',
    synonyms: ['emailoptin', 'emailauthorized', 'emailconsent'],
  },
  {
    key: 'smsOptIn',
    label: 'Text opt-in',
    synonyms: ['smsoptin', 'smsauthorized', 'textoptin', 'smsconsent'],
  },
  {
    key: 'marketingEmail',
    label: 'Marketing email opt-in',
    synonyms: ['emailmarketing', 'marketingemail', 'marketingemailoptin'],
  },
  {
    key: 'marketingSms',
    label: 'Marketing text opt-in',
    synonyms: ['smsmarketing', 'marketingsms', 'marketingtext'],
  },
];

// ─── Catalogs ───────────────────────────────────────────────────────────────

export interface CatalogEntry {
  id: string;
  label: string;
}

export type ImportCatalogs = Partial<Record<LookupCatalog, CatalogEntry[]>> & {
  customFields?: CatalogEntry[];
};

/**
 * The full field list for an account: the built-ins plus one entry per custom
 * lead field, so auto-detection, the dropdown and the claim-once rule all work
 * on custom fields with no special case.
 */
export function buildFieldList(catalogs: ImportCatalogs = {}): LeadImportField[] {
  const custom = (catalogs.customFields ?? []).map<LeadImportField>((entry) => ({
    key: `${CUSTOM_FIELD_PREFIX}${entry.id}`,
    label: entry.label,
    synonyms: [normalizeHeader(entry.label)],
  }));

  return [...LEAD_IMPORT_FIELDS, ...custom];
}

export function fieldByKey(key: string | null, fields = LEAD_IMPORT_FIELDS): LeadImportField | undefined {
  return key === null ? undefined : fields.find((field) => field.key === key);
}

/** The modes a field accepts. Plain text fields accept only `value`. */
export function modesFor(field: LeadImportField | undefined): PopulationMode[] {
  return field?.modes ?? ['value'];
}

export function isCustomField(key: string | null): boolean {
  return key !== null && key.startsWith(CUSTOM_FIELD_PREFIX);
}

export function customFieldId(key: string): string {
  return key.slice(CUSTOM_FIELD_PREFIX.length);
}

// ─── Headings ───────────────────────────────────────────────────────────────

/**
 * Reduce a heading to a comparable form: "First Name", "first_name",
 * "FIRSTNAME" and "First-Name" all become "firstname".
 */
export function normalizeHeader(header: string): string {
  return (header ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

// ─── The mapping ────────────────────────────────────────────────────────────

/** A mapping where every column is plain value mode. Convenience for callers and tests. */
export function simpleMapping(keys: (string | null)[]): ColumnMapping {
  return keys.map((field) => ({ field, mode: 'value' as PopulationMode }));
}

export interface FieldUsage {
  /** Columns using this field in a non-flag mode. */
  value: number;
  /** Columns using this field as a tick box. */
  flag: number;
}

export function fieldUsage(mapping: ColumnMapping): Map<string, FieldUsage> {
  const usage = new Map<string, FieldUsage>();

  mapping.forEach((assignment) => {
    if (assignment.field === null) return;

    const entry = usage.get(assignment.field) ?? { value: 0, flag: 0 };
    if (assignment.mode === 'flag') entry.flag += 1;
    else entry.value += 1;
    usage.set(assignment.field, entry);
  });

  return usage;
}

/**
 * Whether a column may take (field, mode) given everything else in the mapping.
 *
 * A field is used by at most ONE non-flag column, OR by any number of flag
 * columns, never both. Flag mode is inherently many-to-one — LOCATION 1 and
 * LOCATION 2 both feed Facility — so the claim-once rule that protects the
 * other modes has to step aside for it.
 */
export function canAssign(
  mapping: ColumnMapping,
  index: number,
  field: string,
  mode: PopulationMode,
): boolean {
  const others = mapping.filter((_, i) => i !== index);
  const usage = fieldUsage(others).get(field);
  if (!usage) return true;

  return mode === 'flag' ? usage.value === 0 : usage.value === 0 && usage.flag === 0;
}

/**
 * The modes this column could actually take for a field, given every other
 * column. An empty list means the field is unavailable here.
 *
 * The field dropdown, the default mode on picking a field, and the mode
 * dropdown all need this same answer. Asking canAssign about one hard-coded
 * mode cannot express "unavailable as a name, available as a tick box", which
 * is the state every second tick-box column starts in.
 */
export function assignableModes(
  mapping: ColumnMapping,
  index: number,
  fieldKey: string,
  fields: LeadImportField[] = LEAD_IMPORT_FIELDS,
): PopulationMode[] {
  return modesFor(fieldByKey(fieldKey, fields)).filter((mode) =>
    canAssign(mapping, index, fieldKey, mode),
  );
}

// ─── Auto-detection ─────────────────────────────────────────────────────────

export interface DetectionContext {
  catalogs?: ImportCatalogs;
  /** Sample cells per column, used to tell a tick-box column from a text one. */
  samples?: string[][];
  /** The field list to match against; defaults to the built-ins. */
  fields?: LeadImportField[];
}

const BOOLEAN_TRUE = ['true', 'yes', '1', 'on'];
const BOOLEAN_FALSE = ['false', 'no', '0', 'off'];

/**
 * Read a cell as a tick box. Mirrors the server's LeadLookupResolver::toBool
 * and the legacy receiver's literal set exactly — anything else is not a
 * boolean, so a column of them is not a tick-box column.
 */
export function toBool(value: string): boolean | null {
  const normalised = (value ?? '').trim().toLowerCase();
  if (BOOLEAN_TRUE.includes(normalised)) return true;
  if (BOOLEAN_FALSE.includes(normalised)) return false;
  return null;
}

function catalogLabels(catalogs: ImportCatalogs, catalog: LookupCatalog): Map<string, CatalogEntry> {
  const byKey = new Map<string, CatalogEntry>();
  for (const entry of catalogs[catalog] ?? []) {
    byKey.set(normalizeHeader(entry.label), entry);
  }
  return byKey;
}

/** Every sampled non-empty cell in this column reads as a boolean. */
function looksLikeTickBox(samples: string[] | undefined): boolean {
  const filled = (samples ?? []).filter((value) => (value ?? '').trim() !== '');
  if (filled.length === 0) return false;

  return filled.every((value) => toBool(value) !== null);
}

/**
 * Suggest a field and mode for each column.
 *
 * Only ever a suggestion — every row stays editable, because a wrong guess the
 * user cannot override is worse than no guess.
 *
 * Called with just headings it behaves exactly as it always did; the context is
 * what unlocks flag detection and name-vs-id mode selection.
 */
export function autoDetectMapping(
  headers: string[],
  context: DetectionContext = {},
): ColumnMapping {
  const fields = context.fields ?? LEAD_IMPORT_FIELDS;
  const catalogs = context.catalogs ?? {};
  const samples = context.samples ?? [];

  const mapping: ColumnMapping = headers.map(() => ({ field: null, mode: 'value' }));
  const claimed = new Set<string>();
  const flagged = new Set<string>();

  // Pass 0: tick-box columns whose heading names a catalog entry. Requires
  // boolean-looking cells, or a facility literally called "Notes" would hijack
  // a prose Notes column.
  for (const field of fields) {
    if (!field.catalog || !modesFor(field).includes('flag')) continue;

    const labels = catalogLabels(catalogs, field.catalog);
    if (labels.size === 0) continue;

    headers.forEach((header, index) => {
      if (mapping[index].field !== null) return;
      if (claimed.has(field.key)) return;

      const normalized = normalizeHeader(header);
      if (normalized === '' || !labels.has(normalized)) return;
      if (!looksLikeTickBox(samples[index])) return;

      mapping[index] = { field: field.key, mode: 'flag' };
      flagged.add(field.key);
    });
  }

  // Two passes so a generic column cannot take a field that a specific column
  // later in the file would have matched exactly.
  const passes: ((field: LeadImportField, normalized: string) => boolean)[] = [
    (field, normalized) => normalized === normalizeHeader(field.label),
    (field, normalized) => field.synonyms.includes(normalized),
  ];

  for (const matches of passes) {
    headers.forEach((header, index) => {
      if (mapping[index].field !== null) return;

      const normalized = normalizeHeader(header);
      if (normalized === '') return;

      const field = fields.find(
        (candidate) =>
          !claimed.has(candidate.key) &&
          !flagged.has(candidate.key) &&
          matches(candidate, normalized),
      );

      if (!field) return;

      mapping[index] = { field: field.key, mode: defaultModeFor(field, samples[index]) };
      claimed.add(field.key);
    });
  }

  // A "Name" column is only worth using when no explicit name column matched.
  if (claimed.has('firstName') && claimed.has('lastName')) {
    const fullNameIndex = mapping.findIndex((entry) => entry.field === FULL_NAME_KEY);
    if (fullNameIndex !== -1) mapping[fullNameIndex] = { field: null, mode: 'value' };
  }

  return mapping;
}

/** A column of digits is an id; anything else is a name. */
function defaultModeFor(field: LeadImportField, samples: string[] | undefined): PopulationMode {
  const modes = modesFor(field);
  if (!modes.includes('name') && !modes.includes('id')) return modes[0];

  const filled = (samples ?? []).filter((value) => (value ?? '').trim() !== '');
  const allDigits = filled.length > 0 && filled.every((value) => /^\d+$/.test(value.trim()));

  return allDigits && modes.includes('id') ? 'id' : 'name';
}

// ─── Names ──────────────────────────────────────────────────────────────────

/**
 * Split a single name column on the LAST space, so a multi-word first name
 * survives. A single token is treated as a last name, matching how a one-word
 * entry reads on a lead.
 */
export function splitFullName(value: string): { firstName: string; lastName: string } {
  const trimmed = (value ?? '').trim().replace(/\s+/g, ' ');
  if (trimmed === '') return { firstName: '', lastName: '' };

  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace === -1) return { firstName: '', lastName: trimmed };

  return { firstName: trimmed.slice(0, lastSpace), lastName: trimmed.slice(lastSpace + 1) };
}

// ─── Readiness ──────────────────────────────────────────────────────────────

/** Which required fields are still unmatched. Empty means ready to import. */
export function missingRequiredFields(mapping: ColumnMapping): string[] {
  const used = new Set(mapping.map((entry) => entry.field).filter((f): f is string => f !== null));
  if (used.has(FULL_NAME_KEY)) return [];

  const missing: string[] = [];
  if (!used.has('firstName')) missing.push('First name');
  if (!used.has('lastName')) missing.push('Last name');

  return missing;
}

export function isReadyToImport(mapping: ColumnMapping): boolean {
  return missingRequiredFields(mapping).length === 0;
}

// ─── Compiling and building rows ────────────────────────────────────────────

export interface CompiledMapping {
  value: { index: number; field: string }[];
  lookup: { index: number; field: string; by: 'name' | 'id' }[];
  /** field -> its tick-box columns, in file order. */
  flags: { field: string; columns: { index: number; heading: string }[] }[];
  custom: { index: number; contactFieldId: string }[];
}

/**
 * Resolve the mapping against the headings once, before the row loop.
 *
 * Flag mode needs the heading text and a file-order tie-break, both properties
 * of the file rather than of any row, so doing this once keeps buildRow the
 * trivial pure function it should be.
 */
export function compileMapping(mapping: ColumnMapping, headers: string[]): CompiledMapping {
  const compiled: CompiledMapping = { value: [], lookup: [], flags: [], custom: [] };
  const flagsByField = new Map<string, { index: number; heading: string }[]>();

  mapping.forEach((assignment, index) => {
    const { field, mode } = assignment;
    if (field === null) return;

    if (mode === 'flag') {
      const columns = flagsByField.get(field) ?? [];
      columns.push({ index, heading: headers[index] ?? '' });
      flagsByField.set(field, columns);
      return;
    }

    if (mode === 'name' || mode === 'id') {
      compiled.lookup.push({ index, field, by: mode });
      return;
    }

    if (isCustomField(field)) {
      compiled.custom.push({ index, contactFieldId: customFieldId(field) });
      return;
    }

    compiled.value.push({ index, field });
  });

  // File order, not the order the user happened to edit the dropdowns in:
  // "first ticked wins" has to mean the leftmost column in their spreadsheet.
  for (const [field, columns] of flagsByField) {
    compiled.flags.push({ field, columns: [...columns].sort((a, b) => a.index - b.index) });
  }

  return compiled;
}

export type LookupRef =
  | { by: 'name'; value: string }
  | { by: 'id'; value: string }
  | { by: 'flag'; cells: { name: string; value: string }[] };

export interface ImportRowPayload {
  values: Record<string, string>;
  lookups?: Record<string, LookupRef>;
  custom?: Record<string, string>;
}

/**
 * Turn one row of cells into the payload the API expects.
 *
 * Columns mapped to nothing are dropped entirely rather than sent empty, so an
 * unmatched column cannot overwrite anything server-side.
 *
 * Flag cells are sent as-is, with their headings, rather than being resolved
 * here: emptiness is unambiguous under any boolean rule so dropping blanks is
 * safe, but every other judgement — which literals count, which ticked column
 * wins, whether the heading names a real facility — stays on the server, where
 * the catalog is authoritative and the warning is generated.
 */
export function buildRow(compiled: CompiledMapping, cells: string[]): ImportRowPayload {
  const values: Record<string, string> = {};
  const lookups: Record<string, LookupRef> = {};
  const custom: Record<string, string> = {};

  const cellAt = (index: number) => (cells[index] ?? '').trim();

  for (const { index, field } of compiled.value) {
    const value = cellAt(index);
    if (value === '') continue;

    if (field === FULL_NAME_KEY) {
      const { firstName, lastName } = splitFullName(value);
      if (firstName && !values.firstName) values.firstName = firstName;
      if (lastName && !values.lastName) values.lastName = lastName;
      continue;
    }

    values[field] = value;
  }

  for (const { index, field, by } of compiled.lookup) {
    // Raw, untrimmed: normalising is the resolver's job, and it collapses
    // internal whitespace too.
    const raw = cells[index] ?? '';
    if (raw.trim() === '') continue;

    lookups[field] = { by, value: raw };
  }

  for (const { field, columns } of compiled.flags) {
    const ticked = columns
      .map(({ index, heading }) => ({ name: heading, value: cellAt(index) }))
      .filter((cell) => cell.value !== '');

    if (ticked.length > 0) lookups[field] = { by: 'flag', cells: ticked };
  }

  for (const { index, contactFieldId } of compiled.custom) {
    const value = cellAt(index);
    if (value !== '') custom[contactFieldId] = value;
  }

  const payload: ImportRowPayload = { values };
  if (Object.keys(lookups).length > 0) payload.lookups = lookups;
  if (Object.keys(custom).length > 0) payload.custom = custom;

  return payload;
}

export function buildRows(compiled: CompiledMapping, rows: string[][]): ImportRowPayload[] {
  return rows.map((cells) => buildRow(compiled, cells));
}

/**
 * Carry a mapping across a re-parse.
 *
 * Changing the separator re-reads the file and can produce a completely
 * different set of columns. Matching on heading text rather than position keeps
 * the user's choices for every column that survived, and quietly drops the ones
 * that did not — which is far better than silently re-running auto-detection
 * over their hand-corrections.
 */
export function remapPreserving(
  previous: ColumnMapping,
  previousHeaders: string[],
  nextHeaders: string[],
): ColumnMapping {
  const byHeading = new Map<string, ColumnAssignment>();

  previousHeaders.forEach((heading, index) => {
    const assignment = previous[index];
    if (!assignment || assignment.field === null) return;

    const key = normalizeHeader(heading);
    // A duplicated heading keeps only the first choice; guessing between two
    // identical headings would be arbitrary.
    if (key !== '' && !byHeading.has(key)) byHeading.set(key, assignment);
  });

  return nextHeaders.map(
    (heading) => byHeading.get(normalizeHeader(heading)) ?? { field: null, mode: 'value' as PopulationMode },
  );
}

// ─── Mapping-time warnings ──────────────────────────────────────────────────

export interface MappingWarning {
  columnIndex: number;
  message: string;
}

/** How many sample values a name column is checked against. */
const SAMPLE_CHECK_LIMIT = 50;

/**
 * Problems worth showing before the import runs.
 *
 * The catalogs are already loaded for the dropdowns, so telling the user that a
 * heading matches nothing costs nothing and is far more useful here than in the
 * results afterwards. Advisory only — an unmatched value still imports the lead
 * with that field blank.
 */
export function mappingWarnings(
  mapping: ColumnMapping,
  headers: string[],
  rows: string[][],
  catalogs: ImportCatalogs,
  fields: LeadImportField[] = LEAD_IMPORT_FIELDS,
): MappingWarning[] {
  const warnings: MappingWarning[] = [];

  mapping.forEach((assignment, index) => {
    const field = fieldByKey(assignment.field, fields);
    if (!field?.catalog) return;

    const labels = catalogLabels(catalogs, field.catalog);
    if (labels.size === 0) return;

    if (assignment.mode === 'flag') {
      if (!labels.has(normalizeHeader(headers[index] ?? ''))) {
        warnings.push({
          columnIndex: index,
          message: `No ${field.label.toLowerCase()} is called "${headers[index]}". Rows ticking this column will have no ${field.label.toLowerCase()}.`,
        });
      }
      return;
    }

    if (assignment.mode !== 'name') return;

    const samples = rows
      .map((row) => (row[index] ?? '').trim())
      .filter((value) => value !== '')
      .slice(0, SAMPLE_CHECK_LIMIT);

    const misses = samples.filter((value) => !labels.has(normalizeHeader(value)));
    if (misses.length === 0) return;

    warnings.push({
      columnIndex: index,
      message: `${misses.length} of the first ${samples.length} values don't match a ${field.label.toLowerCase()}, for example "${misses[0]}".`,
    });
  });

  return warnings;
}

// ─── Batching ───────────────────────────────────────────────────────────────

/** Split a list into fixed-size batches for posting. */
export function chunk<T>(items: T[], size: number): T[][] {
  if (size <= 0) return [items];

  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size));
  }

  return batches;
}
